import {
  LinkedinAgent,
  LinkedinCampaign,
  LinkedinCampaignObjective,
  LinkedinCampaignStatsEntry,
  LinkedinCostType,
} from "../types/linkedin";

// Baselines are aligned to UTC days, so their timestamps have to be
// truncated in UTC rather than in whatever offset they arrived in.
export const utcDate = (timestamp: string): string =>
  new Date(timestamp).toISOString().slice(0, 10);

// The days in the post period the agent actually drove. The backend drops
// the baseline the agent record remembers, but that record holds only the
// latest: the window can reach back over earlier ones it has since
// overwritten. The cut is strictly after the deactivation day, since the
// toggle out of a baseline fires on the last tick of that day.
export const agentDrivenDays = (
  post: LinkedinCampaignStatsEntry[],
  agent: LinkedinAgent | null,
): LinkedinCampaignStatsEntry[] => {
  const endedAt = agent?.["baseline-last-deactivated-at"];
  if (!endedAt) return post;
  const lastBaselineDay = utcDate(endedAt);
  return post.filter((stat) => stat["start-date"] > lastBaselineDay);
};

export interface CampaignMetrics {
  /** USD. Use totalCostLocal against anything denominated in a budget. */
  totalCost: number;
  totalCostLocal: number;
  totalClicks: number;
  totalImpressions: number;
  totalVideoViews: number;
  avgCPC: number;
  avgCPV: number;
  avgCPM: number;
  avgCTR: number;
  avgViewRate: number;
  avgDailyCost: number;
  avgDailyClicks: number;
  avgDailyVideoViews: number;
  avgDailyImpressions: number;
}

export const calculateMetrics = (
  stats: LinkedinCampaignStatsEntry[],
): CampaignMetrics => {
  const filteredStats = stats.filter((s) => s.cost > 0);

  const {
    totalCost,
    totalCostLocal,
    totalClicks,
    totalImpressions,
    totalVideoViews,
  } = filteredStats.reduce(
    (acc, s) => ({
      totalCost: acc.totalCost + s.cost,
      totalCostLocal: acc.totalCostLocal + s["cost-local"],
      totalClicks: acc.totalClicks + s.clicks,
      totalImpressions: acc.totalImpressions + s.impressions,
      totalVideoViews: acc.totalVideoViews + s["video-views"],
    }),
    {
      totalCost: 0,
      totalCostLocal: 0,
      totalClicks: 0,
      totalImpressions: 0,
      totalVideoViews: 0,
    },
  );

  return {
    totalCost,
    totalCostLocal,
    totalClicks,
    totalImpressions,
    totalVideoViews,
    avgCPC: totalClicks > 0 ? totalCost / totalClicks : 0,
    avgCPV: totalVideoViews > 0 ? totalCost / totalVideoViews : 0,
    avgCPM: totalImpressions > 0 ? (totalCost / totalImpressions) * 1000 : 0,
    avgCTR: totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0,
    avgViewRate:
      totalImpressions > 0 ? (totalVideoViews / totalImpressions) * 100 : 0,
    avgDailyCost: totalCost / Math.max(1, filteredStats.length),
    avgDailyClicks: totalClicks / Math.max(1, filteredStats.length),
    avgDailyVideoViews: totalVideoViews / Math.max(1, filteredStats.length),
    avgDailyImpressions: totalImpressions / Math.max(1, filteredStats.length),
  };
};

export enum LinkedinMetricType {
  CPM = "CPM",
  CPV = "CPV",
  CPC = "CPC",
}

export const getMetricType = (
  objective: LinkedinCampaignObjective | null,
  costType: LinkedinCostType | null,
): LinkedinMetricType => {
  if (costType === LinkedinCostType.CPM) {
    return LinkedinMetricType.CPM;
  }
  if (objective === LinkedinCampaignObjective.VIDEO_VIEWS) {
    return LinkedinMetricType.CPV;
  }
  return LinkedinMetricType.CPC;
};

// The agent works for every campaign objective: LinkedIn allows manual
// bidding (optimizationTargetType NONE) on all of them, and the agent
// converts auto-bid campaigns to manual bidding itself. The only
// campaigns it can't manage are those without a daily budget (LinkedIn
// allows lifetime-budget-only campaigns) — the agent requires one and
// pauses immediately otherwise. A campaign that already has an agent is
// always supported, so its agent page stays reachable.
export const isSupportedLinkedinCampaign = (
  campaign: LinkedinCampaign,
): boolean => campaign["daily-budget"] !== null || campaign.agent !== null;

export const getCostPerOutcome = (
  metrics: CampaignMetrics,
  metricType: LinkedinMetricType,
): number => {
  switch (metricType) {
    case LinkedinMetricType.CPM:
      return metrics.avgCPM;
    case LinkedinMetricType.CPV:
      return metrics.avgCPV;
    case LinkedinMetricType.CPC:
      return metrics.avgCPC;
  }
};

export const getTotalOutcomes = (
  metrics: CampaignMetrics,
  metricType: LinkedinMetricType,
): number => {
  switch (metricType) {
    case LinkedinMetricType.CPM:
      return metrics.totalImpressions;
    case LinkedinMetricType.CPV:
      return metrics.totalVideoViews;
    case LinkedinMetricType.CPC:
      return metrics.totalClicks;
  }
};
